import { Box, Button, Chip, IconButton, Tooltip, Typography, styled } from '@mui/material';
import { BookmarkIcon, DownloadIcon, EditIcon, ExternalLinkIcon, ShieldAlertIcon, Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import resourceItemImage from '../../assets/images/case-file-grid-bg.svg';
import { ResourceStatus } from '../../types/enums/ResourceStatus.enum';
import { ResourceType } from '../../types/enums/ResourceType.enum';
import { Resource } from '../../types/Resources/Resource.type';
import ExternalLinkWarning from '../ExternalLinkWarning/ExternalLinkWarning';

const ResourceCard = styled(Box)({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '36px',
  padding: '18px 24px',
  marginBottom: '16px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.06), 0 1px 3px 0 rgba(0, 0, 0, 0.10)',
});

const ResourceImageWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fff',
  borderRadius: '8px',
  border: '1px solid #cbd5e0',
  boxShadow: '0 3.076px 4.614px rgba(0, 0, 0, 0.05)',
  padding: '8px',
});

const ResourceActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

interface ResourceTableItemProps {
  resource: Resource;
  onEdit: (resource: Resource) => void;
  onPreview: (resource: Resource) => void;
  onDownload: (resource: Resource) => void;
  onFavoriteToggle: (resourceId: number) => void;
  onReview?: (resource: Resource) => void;
  onDelete: (resource: Resource) => void;
}

export function ResourceTableItem({
  resource,
  onEdit,
  onPreview,
  onDownload,
  onFavoriteToggle,
  onReview,
  onDelete,
}: ResourceTableItemProps) {
  const [linkWarningOpen, setLinkWarningOpen] = useState(false);
  const [pendingLink, setPendingLink] = useState('');

  const isLink = resource.type.id === ResourceType.LINK;
  const isApproved = resource.status.id === ResourceStatus.ACTIVE;
  const isPending = resource.status.id === ResourceStatus.DRAFT;
  const canEdit = resource.permissions?.can_edit || false;
  const canReview = resource.permissions?.can_review || false;
  const canDelete = resource.permissions?.can_delete || false;

  const handleVisitClick = () => {
    if (resource.link) {
      setPendingLink(resource.link);
      setLinkWarningOpen(true);
    }
  };

  const handleConfirmLink = () => {
    window.open(pendingLink, '_blank');
    setLinkWarningOpen(false);
    setPendingLink('');
  };

  const handleCancelLink = () => {
    setLinkWarningOpen(false);
    setPendingLink('');
  };

  return (
    <ResourceCard>
      <Tooltip title={resource.is_favorite ? 'Remove from favorites' : 'Add to favorites'}>
        <IconButton
          disableRipple
          onClick={() => onFavoriteToggle(resource.id)}
          sx={{
            padding: 0,
            position: 'absolute',
            top: '-3px',
            right: '24px',
            color: resource.is_favorite ? '#F6AD55' : '#A0AEC0',
          }}
        >
          <BookmarkIcon size={24} fill={resource.is_favorite ? '#F6AD55' : 'none'} />
        </IconButton>
      </Tooltip>

      <ResourceImageWrapper>
        <img src={resourceItemImage} alt='' width={56} height={56} />
      </ResourceImageWrapper>

      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant='body1' sx={{ fontWeight: 600, fontSize: '16px' }}>
            {resource.name}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: '14px' }}>
            {resource.description}
          </Typography>
        </Box>

        <ResourceActions>
          {canReview && isPending && (
            <Button
              variant='contained'
              color='accentYellow'
              startIcon={<ShieldAlertIcon size={18} />}
              size='small'
              onClick={() => onReview?.(resource)}
              disableElevation
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                minWidth: '100px',
                color: '#fff',
              }}
            >
              Review
            </Button>
          )}
          {isPending && !canReview && <Chip sx={{ borderRadius: '6px' }} label='Pending review' size='medium' />}
          {isApproved && (
            <>
              {!isLink && (
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<DownloadIcon size={16} />}
                  onClick={() => onDownload(resource)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    minWidth: '100px',
                  }}
                >
                  Download
                </Button>
              )}
              {isLink ? (
                <Button
                  variant='contained'
                  color='accentBlue'
                  size='small'
                  startIcon={<ExternalLinkIcon size={16} />}
                  onClick={handleVisitClick}
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    minWidth: '100px',
                    color: '#fff',
                  }}
                >
                  Visit
                </Button>
              ) : (
                <Button
                  variant='contained'
                  color='accentBlue'
                  size='small'
                  onClick={() => onPreview(resource)}
                  disableElevation
                  sx={{
                    textTransform: 'none',
                    fontSize: '14px',
                    minWidth: '100px',
                    color: '#fff',
                  }}
                >
                  Preview
                </Button>
              )}
            </>
          )}
          {canEdit && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<EditIcon size={16} />}
              onClick={() => onEdit(resource)}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                minWidth: '80px',
              }}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              variant='outlined'
              size='small'
              color='error'
              startIcon={<Trash2Icon size={16} />}
              onClick={() => onDelete(resource)}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                minWidth: '80px',
              }}
            >
              Delete
            </Button>
          )}
        </ResourceActions>
      </Box>
      <ExternalLinkWarning
        open={linkWarningOpen}
        url={pendingLink}
        onConfirm={handleConfirmLink}
        onCancel={handleCancelLink}
      />
    </ResourceCard>
  );
}

export default ResourceTableItem;
